import {
  FormControl,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  TextField,
} from '@mui/material';
import React from 'react';
import {
  Control,
  Controller,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import Answer from '../components/Answer';
import styles from '../styles.module.scss';
import { ETIAnswerV1, ETIFormData } from '../types';
import drawerStyles from './ETIBuilderDrawer.module.scss';

export interface ETIBuilderQuestionRowProps {
  className?: string;
  control: Control<Omit<ETIFormData, 'taskId' | 'status'>>;
  setValue?: UseFormSetValue<Omit<ETIFormData, 'taskId' | 'status'>>;
  index: number;
  onRemove: () => void;
  isDisabled: boolean;
  isReadOnly: boolean;
  answers: ETIAnswerV1[];
  isVersion2: boolean;
  questionNumber?: number;
}

const ETIBuilderQuestionRow = ({
  control,
  setValue,
  className,
  index,
  onRemove,
  isDisabled,
  isReadOnly,
  answers,
  isVersion2,
  questionNumber,
}: ETIBuilderQuestionRowProps) => {
  const questionType = useWatch({
    control,
    name: `questions.${index}.questionType` as const,
  });

  return (
    <TableRow className={className}>
      <TableCell
        className={styles.NumberCell}
        sx={{
          padding: '2px 4px !important',
          fontSize: '14px !important',
          color: '#6b6a65 !important',
          textAlign: 'center',
        }}
      >
        {questionNumber}
      </TableCell>
      <TableCell className={styles.QuestionTypeCell}>
        <Controller
          control={control}
          name={`questions.${index}.questionType`}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              variant="standard"
              error={!!fieldState.error?.message}
            >
              <Select
                disabled={isDisabled || isReadOnly}
                displayEmpty
                {...field}
                value={field.value || ''}
                onChange={e => {
                  field.onChange(e);
                  const value = e.target.value as string;
                  if (!(value === 'radio' || value === 'checkbox')) {
                    if (setValue) {
                      setValue(`questions.${index}.options`, undefined as any, {
                        shouldDirty: true,
                      });
                      setValue(`questions.${index}.optionsRaw`, '', {
                        shouldDirty: true,
                      });
                      setValue(`questions.${index}.filter`, undefined as any, {
                        shouldDirty: true,
                      });
                      setValue(`questions.${index}.filterRaw`, '', {
                        shouldDirty: true,
                      });
                    }
                  }
                }}
              >
                <MenuItem value="" disabled>
                  <em>Select Type</em>
                </MenuItem>
                <MenuItem value="text">text</MenuItem>
                <MenuItem value="optional_text">optional_text</MenuItem>
                <MenuItem value="radio">radio</MenuItem>
                <MenuItem value="checkbox">checkbox</MenuItem>
                <MenuItem value="attachment">attachment</MenuItem>
              </Select>
              {fieldState.error?.message && (
                <FormHelperText>{fieldState.error.message}</FormHelperText>
              )}
            </FormControl>
          )}
        />
      </TableCell>
      <TableCell className={styles.SectionCell}>
        <Controller
          control={control}
          name={`questions.${index}.section`}
          render={({ field, fieldState }) => (
            <TextField
              disabled={isDisabled}
              fullWidth
              placeholder="Section"
              error={!!fieldState.error?.message}
              helperText={fieldState.error?.message}
              variant="standard"
              slotProps={{ input: { readOnly: isReadOnly } }}
              {...field}
              onBlur={e => {
                field.onBlur();
                if (e.target.value !== e.target.value.trim()) {
                  field.onChange(e.target.value.trim());
                }
              }}
            />
          )}
        />
      </TableCell>
      <TableCell className={styles.Cell}>
        <Controller
          control={control}
          name={`questions.${index}.question`}
          render={({ field, fieldState }) => (
            <TextField
              disabled={isDisabled}
              fullWidth
              multiline
              minRows={1}
              placeholder="Question"
              error={!!fieldState.error?.message}
              id={`eti-question-${index}`}
              helperText={fieldState.error?.message}
              variant="standard"
              slotProps={{ input: { readOnly: isReadOnly } }}
              {...field}
              onBlur={e => {
                field.onBlur();
                if (e.target.value !== e.target.value.trim()) {
                  field.onChange(e.target.value.trim());
                }
              }}
            />
          )}
        />
      </TableCell>
      <TableCell className={styles.Cell}>
        {questionType === 'radio' || questionType === 'checkbox' ? (
          <Controller
            control={control}
            name={`questions.${index}.optionsRaw`}
            render={({ field, fieldState }) => (
              <TextField
                disabled={isDisabled}
                fullWidth
                multiline
                minRows={1}
                variant="standard"
                error={!!fieldState.error?.message}
                helperText={fieldState.error?.message}
                slotProps={{ input: { readOnly: isReadOnly } }}
                {...field}
              />
            )}
          />
        ) : (
          <TextField
            disabled
            fullWidth
            variant="standard"
            placeholder="—"
            value=""
          />
        )}
      </TableCell>
      <TableCell className={styles.Cell}>
        <Controller
          control={control}
          name={`questions.${index}.filterRaw`}
          render={({ field, fieldState }) => (
            <TextField
              disabled={isDisabled}
              fullWidth
              multiline
              minRows={1}
              variant="standard"
              error={!!fieldState.error?.message}
              helperText={fieldState.error?.message}
              slotProps={{ input: { readOnly: isReadOnly } }}
              className={drawerStyles.filterInput}
              {...field}
            />
          )}
        />
      </TableCell>
      {!isReadOnly ? (
        <TableCell className={styles.ActionsCell} align="center">
          <IconButton
            disabled={isDisabled}
            onClick={onRemove}
            className={drawerStyles.deleteBtn}
            aria-label="Delete question"
          >
            ✕
          </IconButton>
        </TableCell>
      ) : (
        !isVersion2 && (
          <TableCell className={styles.Cell}>
            <Answer answer={answers?.[index]} />
          </TableCell>
        )
      )}
    </TableRow>
  );
};

export default React.memo(ETIBuilderQuestionRow);
